/**
 * Vest position, TP and SL lines on `.VEST` charts (#636). Pure: no store, no
 * chart, no socket.
 *
 * SOURCE. tradovate-service's VestPositionWatcher pushes `broker:positions`
 * for each connected Vest broker (every 5 s while a position is open, at once
 * after an order action). Its rows are the broker's SELECTED account only -
 * the account the perps overlay trades (`broker:perps-set-account`) - so copier
 * followers (other accounts of the login) are never in them. Each row is
 * tagged `venue: 'vest'` and carries the market's wire symbol (`vestSymbol`),
 * signed `netPosUnits`, `netPrice` (entry), `takeProfitPrice` /
 * `stopLossPrice` (the first working leg's trigger, absent without one), the
 * Vest mark P&L and the market's `priceDecimals` / `tickSize`.
 *
 * The futures brokerStore never sees these rows (brokerVisibility.ts hides
 * the Vest broker), so nothing here can change a CME chart's lines.
 *
 * PRICE SCALE. A `.VEST` chart is Vest's own trade tape, so entry / TP / SL
 * are drawn as they are: no basis translation.
 *
 * SCOPE. Only on the chart whose market equals the row's market
 * (`BTC-PERP` -> `BTC-PERP.VEST`). The index markets (NQ-PERP / ES-PERP) are
 * never drawn anywhere: they have no `.VEST` chart and their prices are on
 * the mark scale, not the CME one.
 */

import { isVestFeedSymbol, tickDecimals, vestFeedMarket, VEST_FEED_SUFFIX } from './vestFeed';

/** Vest markets that are never drawn: the two index perps, by wire and display name. */
const INDEX_MARKETS = new Set(['NDX-USD-PERP', 'SPX-USD-PERP', 'NQ-PERP', 'ES-PERP']);

export interface VestPositionRow {
  brokerId: string;
  /** Wire symbol, upper case (`BTC-PERP`). */
  vestSymbol: string;
  /** What people call it (`BTC-PERP`, `NQ-PERP`), upper case. */
  displaySymbol: string;
  side: 'long' | 'short';
  /** Absolute size in venue units. */
  units: number;
  entryPrice: number;
  takeProfitPrice: number | null;
  stopLossPrice: number | null;
  /** #639: the account the row is for (the broker's selected one when pushed); sent with chart commands. */
  accountId: number | string | null;
  /** #639: the working legs' Vest ids (what `broker:cancel-order` takes to remove one); null without. */
  takeProfitOrderId: string | null;
  stopLossOrderId: string | null;
  /** The service's mark-based P&L (USD); null when it had no mark. */
  unrealizedPnl: number | null;
  priceDecimals: number | null;
  tickSize: number | null;
}

export type VestPositionsByBroker = Readonly<Record<string, readonly VestPositionRow[]>>;

function finite(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function idText(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() === '' ? null : v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function positivePrice(v: unknown): number | null {
  const n = finite(v);
  return n !== null && n > 0 ? n : null;
}

/** The Vest rows of one `broker:positions` payload, open ones only. Anything malformed is dropped. */
export function parseVestPositionRows(brokerId: string, positions: unknown): VestPositionRow[] {
  if (!Array.isArray(positions)) return [];
  const out: VestPositionRow[] = [];
  for (const p of positions) {
    if (!p || typeof p !== 'object') continue;
    const r = p as Record<string, unknown>;
    if (r.venue !== 'vest') continue;
    const vestSymbol = String(r.vestSymbol ?? r.symbol ?? '').trim().toUpperCase();
    if (!vestSymbol) continue;
    const signed = finite(r.netPosUnits ?? r.netPos);
    if (signed === null || Math.abs(signed) < 1e-12) continue;
    const entryPrice = positivePrice(r.netPrice);
    if (entryPrice === null) continue;
    const decimals = finite(r.priceDecimals);
    const tick = positivePrice(r.tickSize);
    out.push({
      brokerId,
      vestSymbol,
      displaySymbol: String(r.displaySymbol ?? vestSymbol).trim().toUpperCase() || vestSymbol,
      side: signed > 0 ? 'long' : 'short',
      units: Math.abs(signed),
      entryPrice,
      takeProfitPrice: positivePrice(r.takeProfitPrice),
      stopLossPrice: positivePrice(r.stopLossPrice),
      accountId: typeof r.accountId === 'number' && Number.isFinite(r.accountId) ? r.accountId : idText(r.accountId),
      takeProfitOrderId: idText(r.takeProfitOrderId),
      stopLossOrderId: idText(r.stopLossOrderId),
      unrealizedPnl: finite(r.unrealizedPnl),
      priceDecimals: decimals !== null && decimals >= 0 && decimals <= 12 ? Math.round(decimals) : null,
      tickSize: tick,
    });
  }
  return out;
}

/**
 * Apply one `broker:positions` payload. Returns the SAME object when nothing
 * changed for Vest (every futures broker's push), so subscribers do not
 * re-render. A failed read (`success: false`) is "could not look", never
 * "flat": the last rows stay. A Vest broker that went flat is removed.
 */
export function applyVestPositionsEvent(
  state: VestPositionsByBroker,
  payload: { brokerId?: unknown; positions?: unknown; success?: unknown } | null | undefined,
): VestPositionsByBroker {
  if (!payload || typeof payload.brokerId !== 'string' || payload.brokerId === '') return state;
  if (payload.success === false) return state;
  if (!Array.isArray(payload.positions)) return state;
  const brokerId = payload.brokerId;
  const rows = parseVestPositionRows(brokerId, payload.positions);
  if (rows.length === 0) {
    if (!(brokerId in state)) return state;
    const next = { ...state };
    delete next[brokerId];
    return next;
  }
  const prev = state[brokerId];
  if (prev && JSON.stringify(prev) === JSON.stringify(rows)) return state;
  return { ...state, [brokerId]: rows };
}

/** A broker disconnected: its rows go (the engine drops its cached positions too). */
export function removeVestBroker(state: VestPositionsByBroker, brokerId: string): VestPositionsByBroker {
  if (!(brokerId in state)) return state;
  const next = { ...state };
  delete next[brokerId];
  return next;
}

/** `BTC-PERP` -> `BTC-PERP.VEST`; null for an index market (no chart, never drawn). */
export function vestChartSymbolFor(vestSymbol: string): string | null {
  const m = vestSymbol.trim().toUpperCase();
  if (!m || INDEX_MARKETS.has(m)) return null;
  return `${m}${VEST_FEED_SUFFIX}`;
}

/** True when this row belongs on this chart. Never for a non-`.VEST` chart or an index market. */
export function vestRowMatchesChart(row: Pick<VestPositionRow, 'vestSymbol' | 'displaySymbol'>, chartSymbol: string | null | undefined): boolean {
  if (!isVestFeedSymbol(chartSymbol)) return false;
  const market = vestFeedMarket(chartSymbol);
  if (!market || INDEX_MARKETS.has(market)) return false;
  if (INDEX_MARKETS.has(row.vestSymbol) || INDEX_MARKETS.has(row.displaySymbol)) return false;
  return vestChartSymbolFor(row.vestSymbol) === `${market}${VEST_FEED_SUFFIX}`;
}

// --- lines ------------------------------------------------------------------

/** Same greens / reds as the Tradovate LiveOrderLine / LivePositionLine CSS. */
export const VEST_LINE_COLORS = {
  profit: '#34d399',
  loss: '#f87171',
  tp: '#34d399',
  sl: '#f87171',
  neutral: '#9ca3af',
} as const;

/** lightweight-charts LineStyle values (kept numeric so this file needs no chart import). */
const LINE_SOLID = 0;
const LINE_DOTTED = 1;
const LINE_DASHED = 2;
const LINE_LARGE_DASHED = 3;

/**
 * #639: what the chart shows for a leg instead of the pushed row while the
 * user works on it. `dragging` follows the pointer, `draft` is a +TP / +SL
 * line not sent yet, `pending` a request in flight (or sent and waiting for
 * the next positions push). `price: null` = a remove in flight (drawn at the
 * row's price). The row stays the truth: an override is dropped on the next
 * push after its reply, or as soon as the request fails.
 */
export interface VestLegOverride {
  status: 'dragging' | 'draft' | 'pending';
  price: number | null;
}

export type VestLegOverrides = Readonly<Record<string, VestLegOverride>>;

export interface VestPriceLineSpec {
  /** Stable per broker + market + kind, so a moved TP is updated in place. */
  key: string;
  kind: 'entry' | 'tp' | 'sl';
  price: number;
  color: string;
  lineWidth: 1 | 2;
  lineStyle: number;
  title: string;
  /** Price tag on the axis. Absent = shown (#636 lines); the #637 suggestions pass false. */
  axisLabelVisible?: boolean;
}

function decimalsFor(row: VestPositionRow, fallbackTick: number | null): number {
  if (row.priceDecimals !== null) return row.priceDecimals;
  if (row.tickSize !== null) return tickDecimals(row.tickSize);
  if (fallbackTick !== null && fallbackTick > 0) return tickDecimals(fallbackTick);
  return 2;
}

function signed(n: number, decimals: number): string {
  const s = Math.abs(n).toFixed(decimals);
  return `${n < 0 ? '-' : '+'}${s}`;
}

function usd(n: number): string {
  return `${n < 0 ? '-' : '+'}$${Math.abs(n).toFixed(2)}`;
}

function sizeText(units: number): string {
  return String(Number(units.toFixed(8)));
}

/**
 * Open P&L in USD off the chart's last trade (linear perps:
 * (last - entry) x units, signed by side). A last price more than 50% away
 * from the entry is not this market's (a stale price from the previous chart
 * symbol): the service's mark P&L is used instead, else none.
 */
export function vestOpenPnl(row: VestPositionRow, lastPrice: number | null | undefined): number | null {
  const last = typeof lastPrice === 'number' && Number.isFinite(lastPrice) && lastPrice > 0 ? lastPrice : null;
  if (last !== null && Math.abs(last - row.entryPrice) / row.entryPrice <= 0.5) {
    const dir = row.side === 'long' ? 1 : -1;
    return (last - row.entryPrice) * row.units * dir;
  }
  return row.unrealizedPnl;
}

/**
 * The lines to draw on `chartSymbol`: per matching row an entry line
 * ("LONG 0.05 +$12.34"), and a TP / SL line when the position has one
 * ("TP +250.0 +$12.50": distance from the entry in the market's price
 * decimals, signed for the position, and the USD at that price). Empty for
 * every non-`.VEST` chart.
 */
export function deriveVestPositionLines(
  rows: readonly VestPositionRow[],
  chartSymbol: string | null | undefined,
  lastPrice: number | null | undefined,
  fallbackTick: number | null = null,
  overrides: VestLegOverrides | null = null,
): VestPriceLineSpec[] {
  if (!isVestFeedSymbol(chartSymbol)) return [];
  const out: VestPriceLineSpec[] = [];
  for (const row of rows) {
    if (!vestRowMatchesChart(row, chartSymbol)) continue;
    const decimals = decimalsFor(row, fallbackTick);
    const dir = row.side === 'long' ? 1 : -1;
    const base = `${row.brokerId}:${row.vestSymbol}`;
    const pnl = vestOpenPnl(row, lastPrice);
    out.push({
      key: `${base}:entry`,
      kind: 'entry',
      price: row.entryPrice,
      color: pnl === null ? VEST_LINE_COLORS.neutral : pnl >= 0 ? VEST_LINE_COLORS.profit : VEST_LINE_COLORS.loss,
      lineWidth: 2,
      lineStyle: LINE_SOLID,
      title: `${row.side === 'long' ? 'LONG' : 'SHORT'} ${sizeText(row.units)}${pnl === null ? '' : ` ${usd(pnl)}`}`,
    });
    const leg = (kind: 'tp' | 'sl', rowPrice: number | null) => {
      const ov = overrides?.[`${base}:${kind}`];
      const price = ov && ov.price !== null ? ov.price : rowPrice;
      if (price === null) return;
      const distance = (price - row.entryPrice) * dir;
      const lineStyle = !ov ? LINE_DASHED : ov.status === 'draft' ? LINE_LARGE_DASHED : LINE_DOTTED;
      const suffix = !ov ? '' : ov.status === 'draft' ? ' (drop to send)' : ov.status === 'pending' ? ' ...' : '';
      out.push({
        key: `${base}:${kind}`,
        kind,
        price,
        color: ov?.status === 'pending' ? VEST_LINE_COLORS.neutral : kind === 'tp' ? VEST_LINE_COLORS.tp : VEST_LINE_COLORS.sl,
        lineWidth: 1,
        lineStyle,
        title: `${kind.toUpperCase()} ${signed(distance, decimals)} ${usd(distance * row.units)}${suffix}`,
      });
    };
    leg('tp', row.takeProfitPrice);
    leg('sl', row.stopLossPrice);
  }
  return out;
}

// --- syncing onto a series ----------------------------------------------------

/** What `syncVestPriceLines` needs from a lightweight-charts series. */
export interface PriceLineHost<L> {
  createPriceLine(options: {
    price: number; color: string; lineWidth: 1 | 2; lineStyle: number;
    axisLabelVisible: boolean; title: string;
  }): L;
  removePriceLine(line: L): void;
}

export interface PriceLineUpdatable {
  applyOptions(options: Partial<{ price: number; color: string; lineWidth: 1 | 2; lineStyle: number; title: string }>): void;
}

/**
 * Make `held` (key -> line) match `specs` on `series`: create new lines,
 * update moved / relabelled ones in place, remove the rest. Lines are plain
 * price lines - no handles, nothing to drag or click.
 */
export function syncVestPriceLines<L extends PriceLineUpdatable>(
  series: PriceLineHost<L>,
  held: Map<string, { line: L; spec: VestPriceLineSpec }>,
  specs: readonly VestPriceLineSpec[],
): void {
  const wanted = new Set(specs.map(s => s.key));
  for (const [key, { line }] of held) {
    if (wanted.has(key)) continue;
    try { series.removePriceLine(line); } catch { /* series already gone */ }
    held.delete(key);
  }
  for (const spec of specs) {
    const have = held.get(spec.key);
    if (!have) {
      const line = series.createPriceLine({
        price: spec.price,
        color: spec.color,
        lineWidth: spec.lineWidth,
        lineStyle: spec.lineStyle,
        axisLabelVisible: spec.axisLabelVisible ?? true,
        title: spec.title,
      });
      held.set(spec.key, { line, spec });
      continue;
    }
    const prev = have.spec;
    if (prev.price !== spec.price || prev.color !== spec.color || prev.title !== spec.title
      || prev.lineWidth !== spec.lineWidth || prev.lineStyle !== spec.lineStyle) {
      have.line.applyOptions({
        price: spec.price, color: spec.color, title: spec.title, lineWidth: spec.lineWidth, lineStyle: spec.lineStyle,
      });
      have.spec = spec;
    }
  }
}

/** Remove every held line (series change, unmount, symbol change). */
export function clearVestPriceLines<L>(series: PriceLineHost<L> | null, held: Map<string, { line: L }>): void {
  if (series) {
    for (const { line } of held.values()) {
      try { series.removePriceLine(line); } catch { /* series already gone */ }
    }
  }
  held.clear();
}
